import { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  ArrowTopRightOnSquareIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

export function getImageUrl(image) {
  if (typeof image === "string") {
    return image;
  }
  return image?.url ?? image?.image_url ?? image?.full_url ?? null;
}

export function getImagePath(image) {
  if (typeof image === "string") {
    return null;
  }
  return image?.path ?? null;
}

export default function ReceivingInvoiceImages({
  images,
  onAskDeleteInvoiceImage,
}) {
  const [selectedImage, setSelectedImage] = useState(null);

  const imageItems = (images ?? [])
    .map((image, index) => ({
      url: getImageUrl(image),
      path: getImagePath(image),
      key: image?.id ?? image?.path ?? index,
    }))
    .filter((image) => image.url);

  if (imageItems.length === 0) {
    return null;
  }

  const openInNewTab = () => {
    if (!selectedImage?.url) return;
    window.open(selectedImage.url, "_blank", "noopener,noreferrer");
  };

  const onDeleteClicked = () => {
    if (!selectedImage || !onAskDeleteInvoiceImage) return;
    setSelectedImage(null);
    onAskDeleteInvoiceImage(selectedImage);
  };

  return (
    <>
      <div className="p-4 card-custom">
        <p className="block text-sm font-medium text-gray-900 mb-3">
          Invoice Images
        </p>
        <div className="flex flex-wrap gap-3">
          {imageItems.map((image) => (
            <button
              key={image.key}
              type="button"
              className="rounded-md border border-gray-200 overflow-hidden hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-sky-500"
              onClick={() => setSelectedImage(image)}
            >
              <img
                src={image.url}
                alt="Invoice"
                className="h-32 w-32 object-cover"
              />
            </button>
          ))}
        </div>
      </div>

      <Transition.Root show={selectedImage !== null} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setSelectedImage(null)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/90" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="relative max-h-[90vh] max-w-[90vw]">
                  <div className="absolute -top-10 right-0 flex items-center gap-2">
                    <button
                      type="button"
                      className="rounded-md p-1 text-white hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-white"
                      onClick={openInNewTab}
                    >
                      <span className="sr-only">Open in new tab</span>
                      <ArrowTopRightOnSquareIcon
                        className="h-7 w-7"
                        aria-hidden="true"
                      />
                    </button>
                    {onAskDeleteInvoiceImage ? (
                      <button
                        type="button"
                        className="rounded-md p-1 text-white hover:text-red-300 focus:outline-none focus:ring-2 focus:ring-white"
                        onClick={onDeleteClicked}
                      >
                        <span className="sr-only">Delete</span>
                        <TrashIcon className="h-7 w-7" aria-hidden="true" />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="rounded-md p-1 text-white hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-white"
                      onClick={() => setSelectedImage(null)}
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-8 w-8" aria-hidden="true" />
                    </button>
                  </div>
                  <img
                    src={selectedImage?.url ?? ""}
                    alt="Invoice full size"
                    className="max-h-[90vh] max-w-[90vw] object-contain"
                  />
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}
